import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Check, Send, X, Clock } from "lucide-react";
import { LEVEL_LABEL, LEVEL_TONE, resolveLevel } from "../constants/levels";
import RequirementChips from "./RequirementChips";
import { shortId } from "@/utils/formatText";

// Per apply_state: the passive pill the tab always showed for "applied", a new
// passive pill for "awaiting the candidate's own consent" (no button — this
// codebase has no resend action, so there is nothing to press), or an Apply
// button for everything else. `canApply` additionally requires
// CANDIDATE_UPDATE — a viewer without it sees the tags but never the button.
const JobFitCard = ({ job, canApply, onApply }) => {
  const [reasonOpen, setReasonOpen] = useState(false);
  const level = resolveLevel(job);
  const tone = LEVEL_TONE[level];
  // The model is asked for 1-3 sentences but regularly returns a full
  // paragraph, and three of those fill the pane. Clamp so the list stays
  // scannable, with the full text one click away.
  const reasonIsLong = (job.reason?.length || 0) > 240;
  const applyState = job.apply_state || (job.already_applied ? "applied" : "not_applied");

  return (
    // The left stripe repeats the fit band as pure colour, so the list can be
    // skimmed for strong matches without reading a single label.
    <Card className={`relative overflow-hidden border-border-primary p-4 pl-5`}>
      <span className={`absolute inset-y-0 left-0 w-1.5 ${tone.stripe}`} aria-hidden="true" />

      <div className="mb-2 flex items-start justify-between gap-3">
        <h3
          className="min-w-0 flex-1 truncate text-sm font-semibold text-text-primary"
          title={job.job_id != null ? `${job.job_title} (${shortId(job.job_id)})` : job.job_title}
        >
          {job.job_id ? (
            <Link to={`/admin/job-detail/${job.job_id}`} className="hover:text-text-blue hover:underline">
              {job.job_title}
            </Link>
          ) : (
            job.job_title
          )}
          {job.job_id != null && <span className="ml-1 font-mono text-xs font-normal text-text-secondary">({shortId(job.job_id)})</span>}
        </h3>
        <div className="flex shrink-0 items-center gap-1.5">
          {applyState === "applied" && (
            <span className="rounded-full border border-tone-blue/40 bg-tone-blue-soft px-2 py-0.5 text-xs font-medium text-text-primary">Applied</span>
          )}
          {applyState === "awaiting_candidate_consent" && (
            <span className="inline-flex items-center gap-1 rounded-full border border-tone-amber/40 bg-tone-amber-soft px-2 py-0.5 text-xs font-medium text-text-primary">
              <Clock size={11} aria-hidden="true" />
              Awaiting candidate consent
            </span>
          )}
          {applyState === "not_applied" && canApply && (
            <Button
              onClick={() => onApply?.(job)}
              className="inline-flex items-center gap-1 rounded-md bg-button-secondary px-3 py-1 text-xs font-semibold text-text-base shadow-sm transition-colors hover:bg-button-secondary/80"
            >
              <Send size={11} aria-hidden="true" />
              Apply
            </Button>
          )}
          <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold text-text-primary ${tone.bg} ${tone.border}`}>
            <span className={`h-2 w-2 rounded-full ${tone.stripe}`} aria-hidden="true" />
            {LEVEL_LABEL[level]}
          </span>
        </div>
      </div>

      {job.reason && (
        <div className="mb-3">
          <p className={`text-xs leading-relaxed text-text-secondary ${reasonIsLong && !reasonOpen ? "line-clamp-3" : ""}`}>{job.reason}</p>
          {reasonIsLong && (
            <Button
              onClick={() => setReasonOpen((v) => !v)}
              className="mt-1 rounded-none p-0 text-xs font-medium text-text-secondary underline-offset-2 transition-colors hover:text-text-primary hover:underline"
            >
              {reasonOpen ? "Show less" : "Show more"}
            </Button>
          )}
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <RequirementChips items={job.matched_requirements} tone={LEVEL_TONE.high} icon={Check} label="Requirements matched" />
        <RequirementChips items={job.missing_requirements} tone={LEVEL_TONE.low} icon={X} label="Requirements missing" />
      </div>
    </Card>
  );
};

export default JobFitCard;
